import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sticky from 'react-stickynode';
import { STICKY_STATUS } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { ScrollArrows } from 'site-modules/comparator/components/scroll-arrows/scroll-arrows';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './comparator-grid-sticky-scroll.scss';

export function comparatorGridStickyScroll(WrappedComponent, { withArrows } = {}) {
  function ComparatorGridStickScroll(props) {
    const comparatorGridRef = useRef();
    const comparatorHeaderRef = useRef();
    const comparatorHeaderStickyRef = useRef();
    const [arrows, setArrows] = useState({ left: false, right: false });

    const checkArrows = useDebounce(() => {
      if (!withArrows) {
        return;
      }

      const scrollContainer = comparatorGridRef.current;
      setArrows({
        left: !!scrollContainer.scrollLeft,
        right: scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollContainer.scrollLeft > 1,
      });
    }, 25);

    const onArrowClick = useCallback(isRightArrow => {
      comparatorGridRef.current.scrollLeft = isRightArrow ? comparatorGridRef.current.scrollWidth : 0;
    }, []);

    const syncCarHeaderScroll = useCallback(() => {
      comparatorHeaderRef.current.scrollLeft = comparatorGridRef.current.scrollLeft;
      if (comparatorHeaderStickyRef.current) {
        comparatorHeaderStickyRef.current.scrollLeft = comparatorGridRef.current.scrollLeft;
      }
      checkArrows();
    }, [checkArrows]);

    const onStickyStateChange = useCallback(
      ({ status }) => {
        if (status === Sticky.STATUS_FIXED || status === STICKY_STATUS.STICKY) {
          syncCarHeaderScroll();
        }
      },
      [syncCarHeaderScroll]
    );

    useEffect(() => {
      checkArrows();
    }, [checkArrows]);

    const arrowsProp = useMemo(() => ({ left: arrows.left, right: arrows.right }), [arrows.left, arrows.right]);

    return (
      <div className="container px-0 pos-r">
        <div className="comparator-grid-sticky-scroll px-0_5" onScroll={syncCarHeaderScroll} ref={comparatorGridRef}>
          <WrappedComponent
            {...props}
            arrows={arrowsProp}
            onArrowClick={onArrowClick}
            onStickyStateChange={onStickyStateChange}
            comparatorHeaderRef={comparatorHeaderRef}
            comparatorHeaderStickyRef={comparatorHeaderStickyRef}
          />
        </div>
        {!!withArrows && (
          <ClientSideRender>
            <ScrollArrows heightRef={comparatorHeaderRef} onArrowClick={onArrowClick} arrows={arrowsProp} />
          </ClientSideRender>
        )}
      </div>
    );
  }

  ComparatorGridStickScroll.displayName = `ComparatorGridStickScroll(${WrappedComponent.name})`;
  return ComparatorGridStickScroll;
}
